import React, { useContext, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { Delete, InfoOutlined, Update } from '@material-ui/icons';
import { css } from '@emotion/react';
import { cloneDeep, get, set } from 'lodash';
import * as Sentry from '@sentry/react';
import { Body, GreyBody, DebouncedTextField, OnwardChip, PrimaryButton, SpacerV1 } from '@/styles/blocks';
import { greens, oranges, reds } from '@/styles/colors';
import { UserContext } from '@/components/App';
import { post } from '@/utilities/onwardClient';
import { UPLOAD_INTEGRATION_SECRET } from '@/constants/apiRoutes';
import { INTEGRATION_TYPE_LABELS, INTEGRATION_CONFIG_FIELDS, SHIPPER_SCOPED_OVERRIDE_TYPES } from './constants';
import { IntegrationBlock, FieldRow, EditActions, CopyableId } from './blocks';
import { CorpAdminContext } from '../store';
import { DELETE_CORP_INTEGRATION_OVERRIDE, GET_CORP_INTEGRATION_OVERRIDES } from '../graphql';
import { UPDATE_INTEGRATION_OVERRIDE_BY_PK } from '@/graphql/mutations/integration_overrides';
import { useClientUser } from '@/hooks';

const InlineLabel = ({ label, value }) => (
    <FieldRow
        css={css`
            gap: 0.25rem;
            margin-bottom: 0;
            align-items: center;
        `}
    >
        <GreyBody
            css={css`
                margin-bottom: 0;
            `}
        >
            {label}:
        </GreyBody>
        <Body>{value}</Body>
    </FieldRow>
);

const getFieldPath = (field) => field.path || [field.key];
const getFieldDraft = (field, configDraft, defaultOrderDraft) =>
    field.target === 'default_order' ? defaultOrderDraft : configDraft;

// For fields with a fixed prefix (e.g. PC Miler `PCM`): the user types only the suffix while
// the full prefixed value is stored. Derives the editable suffix from the stored value.
const stripPrefix = (prefix, value) => String(value ?? '').replace(new RegExp(`^\\s*${prefix}\\s*`, 'i'), '');

const setObjectPath = (setter, path, value) =>
    setter((prev) => {
        const next = cloneDeep(prev);
        set(next, path, value);
        return next;
    });

const DeleteIntegrationButton = ({ integration, orgShipperIds, onDeleted, onError }) => {
    const [deleteIntegration, { loading: deleting }] = useMutation(DELETE_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data }) {
            const deletedId = data?.deleted_override?.override_id;
            if (!deletedId) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                    data: {
                        integration_overrides: existing.integration_overrides.filter(
                            (o) => o.override_id !== deletedId
                        ),
                    },
                });
            } catch {
                // cache miss is non-fatal
            }
        },
    });

    const handleDelete = async (e) => {
        e?.stopPropagation?.();
        if (!window.confirm('Delete this integration?')) return;
        try {
            const hookIds = (integration.subscriptions || []).map((s) => s.event_subscription_id).filter(Boolean);
            await deleteIntegration({
                variables: { override_id: integration.override_id, hook_ids: hookIds },
            });
            onDeleted?.();
        } catch (err) {
            Sentry.captureException(err);
            onError?.(err);
        }
    };

    return (
        <Tooltip title="Delete integration" placement="top">
            <span
                css={css`
                    margin-left: auto;
                `}
            >
                <IconButton
                    size="small"
                    onClick={handleDelete}
                    disabled={deleting}
                    css={css`
                        color: ${reds.primary};
                    `}
                >
                    <Delete fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
};

const ManualSetupRow = ({ integration }) => {
    const { admin_clients } = useContext(UserContext);
    const apolloClient = useApolloClient();
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const [pickedType, setPickedType] = useState('');
    const [draft, setDraft] = useState({});
    const [defaultOrderDraft, setDefaultOrderDraft] = useState({});
    const [secrets, setSecrets] = useState({});
    const [jsonbText, setJsonbText] = useState({});
    const [saving, setSaving] = useState(false);

    const updateCache = (savedOverride) => {
        if (!savedOverride?.override_id) return;
        try {
            const existing = apolloClient.readQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
            });
            if (!existing) return;
            apolloClient.writeQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
                data: {
                    integration_overrides: existing.integration_overrides.map((o) =>
                        o.override_id === savedOverride.override_id ? { ...o, ...savedOverride } : o
                    ),
                },
            });
        } catch {
            // cache miss is non-fatal
        }
    };

    const fields = INTEGRATION_CONFIG_FIELDS[pickedType] || [];

    const resetForType = (nextType) => {
        setPickedType(nextType);
        setDraft({});
        setDefaultOrderDraft({});
        setSecrets({});
        setJsonbText({});
    };

    const setFieldValue = (field, value) =>
        setObjectPath(field.target === 'default_order' ? setDefaultOrderDraft : setDraft, getFieldPath(field), value);

    const getFieldValue = (field) => get(getFieldDraft(field, draft, defaultOrderDraft), getFieldPath(field));

    const handleJsonbChange = (field, text) => {
        const { key } = field;
        setJsonbText((prev) => ({ ...prev, [key]: text }));
        try {
            const parsed = text.trim() ? JSON.parse(text) : null;
            setFieldValue(field, parsed);
        } catch {
            // invalid — leave draft as-is until valid
        }
    };

    const isJsonbValid = (key) => {
        const text = jsonbText[key] ?? '';
        if (!text.trim()) return true;
        try {
            JSON.parse(text);
            return true;
        } catch {
            return false;
        }
    };

    const anyInvalidJsonb = fields.some((f) => f.type === 'jsonb' && !isJsonbValid(f.key));

    const handleSave = async () => {
        setSaving(true);
        try {
            const secretEntries = fields
                .filter((f) => f.secret)
                .map((f) => ({ key: f.key, value: secrets[f.key] }))
                .filter((s) => s.value && s.value.trim());

            const body = {
                override: {
                    override_id: integration.override_id,
                    carrier_id: integration.carrier_id,
                    shipper_id: integration.shipper_id,
                    client_id: integration.client_id,
                    middle_mile_carrier_id: integration.middle_mile_carrier_id,
                    integration_meta_type_id: integration.integration_meta_type_id,
                    type: pickedType,
                    config: draft,
                    default_order: defaultOrderDraft,
                },
                secrets: secretEntries,
            };
            const result = await post(UPLOAD_INTEGRATION_SECRET, body);
            updateCache(result?.data?.data);
            setNotification?.({ severity: 'success', message: 'Integration set up' });
        } catch (err) {
            // Log details for diagnostics; show only a generic message to the user.
            console.error('Integration setup failed', err?.response?.data || err);
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to set up integration' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <IntegrationBlock>
            <FieldRow>
                <CopyableId label="Integration ID" value={integration.override_id} />
                <span
                    css={css`
                        color: ${oranges.primary};
                        font-weight: 600;
                        font-size: 12px;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                    `}
                >
                    Needs Setup
                </span>
                <DeleteIntegrationButton
                    integration={integration}
                    orgShipperIds={orgShipperIds}
                    onDeleted={() => setNotification?.({ severity: 'success', message: 'Integration deleted' })}
                    onError={() => setNotification?.({ severity: 'error', message: 'Failed to delete integration' })}
                />
            </FieldRow>
            <SpacerV1 />
            <TextField
                select
                label="Type"
                size="small"
                variant="outlined"
                value={pickedType}
                onChange={(e) => resetForType(e.target.value)}
                css={css`
                    min-width: 240px;
                `}
            >
                <MenuItem value="">
                    <em>Select a type</em>
                </MenuItem>
                {Object.keys(INTEGRATION_CONFIG_FIELDS)
                    .filter((value) => !SHIPPER_SCOPED_OVERRIDE_TYPES.includes(value))
                    .map((value) => (
                        <MenuItem key={value} value={value}>
                            {INTEGRATION_TYPE_LABELS[value] || value}
                        </MenuItem>
                    ))}
            </TextField>
            {pickedType && fields.length > 0 && (
                <div
                    css={css`
                        margin-top: 0.75rem;
                    `}
                >
                    {fields.map((f) => {
                        const fieldPath = getFieldPath(f);
                        const tooltipAdornment = f.tooltip ? (
                            <Tooltip title={f.tooltip} placement="top">
                                <InfoOutlined
                                    css={css`
                                        font-size: 14px;
                                        color: #8d8d8d;
                                        cursor: help;
                                    `}
                                />
                            </Tooltip>
                        ) : null;

                        if (f.type === 'integer') {
                            const val = getFieldValue(f);
                            const display = val !== undefined && val !== null ? String(val) : '';
                            return (
                                <DebouncedTextField
                                    key={f.key}
                                    label={f.label}
                                    size="small"
                                    variant="outlined"
                                    value={display}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const parsed = raw === '' ? null : parseInt(raw, 10);
                                        setFieldValue(f, Number.isNaN(parsed) ? null : parsed);
                                    }}
                                    inputProps={{ type: 'number', step: 1 }}
                                    css={css`
                                        margin-bottom: 0.5rem;
                                    `}
                                />
                            );
                        }

                        if (f.type === 'jsonb') {
                            const text = jsonbText[f.key] ?? '';
                            const valid = isJsonbValid(f.key);
                            return (
                                <DebouncedTextField
                                    key={f.key}
                                    label={f.label}
                                    size="small"
                                    variant="outlined"
                                    multiline
                                    minRows={3}
                                    maxRows={12}
                                    value={text}
                                    onChange={(e) => handleJsonbChange(f, e.target.value)}
                                    error={!valid}
                                    helperText={!valid ? 'Invalid JSON' : undefined}
                                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                                    css={css`
                                        margin-bottom: 0.5rem;
                                        width: 100%;
                                    `}
                                />
                            );
                        }

                        if (f.type === 'boolean') {
                            return (
                                <FormControlLabel
                                    key={f.key}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!getFieldValue(f)}
                                            onChange={(e) => setFieldValue(f, e.target.checked)}
                                        />
                                    }
                                    label={f.label}
                                    css={css`
                                        display: flex;
                                        margin-bottom: 0.5rem;
                                    `}
                                />
                            );
                        }

                        if (f.secret) {
                            return (
                                <DebouncedTextField
                                    key={f.key}
                                    label={f.label}
                                    size="small"
                                    variant="outlined"
                                    type="password"
                                    autoComplete="new-password"
                                    value={secrets[f.key] ?? ''}
                                    onChange={(e) => setSecrets((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                    css={css`
                                        margin-bottom: 0.5rem;
                                    `}
                                />
                            );
                        }

                        if (f.prefix) {
                            return (
                                <DebouncedTextField
                                    key={f.key}
                                    label={f.label}
                                    size="small"
                                    variant="outlined"
                                    value={stripPrefix(f.prefix, getFieldValue(f))}
                                    onChange={(e) => {
                                        const rest = stripPrefix(f.prefix, e.target.value);
                                        setFieldValue(f, rest ? `${f.prefix}${rest}` : '');
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">{f.prefix}</InputAdornment>,
                                        ...(tooltipAdornment ? { endAdornment: tooltipAdornment } : {}),
                                    }}
                                    css={css`
                                        margin-bottom: 0.5rem;
                                    `}
                                />
                            );
                        }

                        return (
                            <DebouncedTextField
                                key={f.key}
                                label={f.label}
                                size="small"
                                variant="outlined"
                                value={get(getFieldDraft(f, draft, defaultOrderDraft), fieldPath) ?? ''}
                                onChange={(e) => setFieldValue(f, e.target.value)}
                                InputProps={tooltipAdornment ? { endAdornment: tooltipAdornment } : undefined}
                                css={css`
                                    margin-bottom: 0.5rem;
                                `}
                            />
                        );
                    })}
                </div>
            )}
            <EditActions>
                <PrimaryButton
                    loading={saving}
                    disabled={!pickedType || saving || anyInvalidJsonb}
                    onClick={handleSave}
                >
                    Save
                </PrimaryButton>
            </EditActions>
        </IntegrationBlock>
    );
};

const ConfiguredIntegrationRow = ({ integration }) => {
    const { admin_clients } = useContext(UserContext);
    const { isOnwardEngineer } = useClientUser();
    const client = useApolloClient();
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;

    const [markComplete, { loading: markingComplete }] = useMutation(UPDATE_INTEGRATION_OVERRIDE_BY_PK, {
        onError: (e) => {
            Sentry.captureException(e);
            setNotification?.({ severity: 'error', message: 'Failed to mark integration complete' });
        },
        onCompleted: () => setNotification?.({ severity: 'success', message: 'Integration marked complete' }),
    });

    const [configDraft, setConfigDraft] = useState(() => cloneDeep(integration.config || {}));
    const [defaultOrderDraft, setDefaultOrderDraft] = useState(() => cloneDeep(integration.default_order || {}));
    const [saving, setSaving] = useState(false);
    const [secrets, setSecrets] = useState({});

    // Raw text state for jsonb fields — kept in sync with configDraft only when valid JSON.
    const [jsonbText, setJsonbText] = useState(() => {
        const init = {};
        const configFields = INTEGRATION_CONFIG_FIELDS[integration.type] || [];
        configFields
            .filter((f) => f.type === 'jsonb')
            .forEach((f) => {
                const val = get(
                    f.target === 'default_order' ? integration.default_order : integration.config,
                    getFieldPath(f)
                );
                init[f.key] = val !== undefined && val !== null ? JSON.stringify(val, null, 2) : '';
            });
        return init;
    });

    const setFieldValue = (field, value) =>
        setObjectPath(
            field.target === 'default_order' ? setDefaultOrderDraft : setConfigDraft,
            getFieldPath(field),
            value
        );

    const getFieldValue = (field) => get(getFieldDraft(field, configDraft, defaultOrderDraft), getFieldPath(field));

    const handleJsonbChange = (field, text) => {
        const { key } = field;
        setJsonbText((prev) => ({ ...prev, [key]: text }));
        try {
            const parsed = text.trim() ? JSON.parse(text) : null;
            setFieldValue(field, parsed);
        } catch {
            // Invalid JSON — don't update the persisted draft.
        }
    };

    const handleIntegerChange = (field, raw) => {
        const val = raw === '' ? null : parseInt(raw, 10);
        setFieldValue(field, Number.isNaN(val) ? null : val);
    };

    const isJsonbValid = (key) => {
        const text = jsonbText[key] ?? '';
        if (!text.trim()) return true;
        try {
            JSON.parse(text);
            return true;
        } catch {
            return false;
        }
    };

    // org shipper IDs needed for the cache read/write
    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const updateCache = (savedOverride) => {
        if (!savedOverride?.override_id) return;
        try {
            const existing = client.readQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
            });
            if (!existing) return;
            client.writeQuery({
                query: GET_CORP_INTEGRATION_OVERRIDES,
                variables: { orgShipperIds },
                data: {
                    integration_overrides: existing.integration_overrides.map((o) =>
                        o.override_id === savedOverride.override_id ? { ...o, ...savedOverride } : o
                    ),
                },
            });
        } catch {
            // Cache miss is non-fatal
        }
    };

    const callSaveFunction = async () => {
        setSaving(true);
        try {
            const secretEntries = Object.entries(secrets)
                .filter(([, v]) => v && v.trim())
                .map(([key, value]) => ({ key, value }));
            const body = {
                override: {
                    override_id: integration.override_id,
                    carrier_id: integration.carrier_id,
                    shipper_id: integration.shipper_id,
                    client_id: integration.client_id,
                    middle_mile_carrier_id: integration.middle_mile_carrier_id,
                    integration_meta_type_id: integration.integration_meta_type_id,
                    type: integration.type,
                    config: configDraft,
                    default_order: defaultOrderDraft,
                },
            };
            if (secretEntries.length > 0) body.secrets = secretEntries;
            const result = await post(UPLOAD_INTEGRATION_SECRET, body);
            const savedOverride = result?.data?.data;
            const updatedConfig = savedOverride?.config ?? configDraft;
            const updatedDefaultOrder = savedOverride?.default_order ?? defaultOrderDraft;
            setConfigDraft(cloneDeep(updatedConfig));
            setDefaultOrderDraft(cloneDeep(updatedDefaultOrder));
            setSecrets({});
            updateCache(savedOverride);
            setNotification({ severity: 'success', message: 'Integration saved.' });
        } catch (err) {
            console.error('Integration save failed', err?.response?.data || err);
            Sentry.captureException(err);
            setNotification({ severity: 'error', message: 'Failed to save integration' });
        } finally {
            setSaving(false);
        }
    };

    const isEdi = !!(integration.rec_isa_id || integration.send_isa_id);
    const configFields = INTEGRATION_CONFIG_FIELDS[integration.type];

    return (
        <IntegrationBlock>
            <FieldRow>
                <CopyableId label="Integration ID" value={integration.override_id} />
                <InlineLabel label="Type" value={INTEGRATION_TYPE_LABELS[integration.type] ?? integration.type} />
                {integration.poll && (
                    <Tooltip title="Orders with this integration are only updated every 30-60min" placement="top">
                        <span>
                            <OnwardChip
                                label="Polling"
                                size="small"
                                icon={
                                    <Update
                                        css={css`
                                            font-size: 14px;
                                        `}
                                    />
                                }
                            />
                        </span>
                    </Tooltip>
                )}
                {isEdi && (
                    <>
                        {integration.rec_isa_id && <InlineLabel label="Rec ISA ID" value={integration.rec_isa_id} />}
                        {integration.send_isa_id && <InlineLabel label="Send ISA ID" value={integration.send_isa_id} />}
                    </>
                )}
                {integration.status ? (
                    <OnwardChip
                        size="small"
                        label={integration.status}
                        style={
                            integration.status === 'COMPLETE'
                                ? { backgroundColor: `${greens.primary}24`, color: greens.primary }
                                : integration.status === 'PENDING'
                                ? { backgroundColor: `${oranges.primary}24`, color: oranges.primary }
                                : { backgroundColor: `${reds.primary}24`, color: reds.primary }
                        }
                    />
                ) : null}
                {isOnwardEngineer && integration.status !== 'COMPLETE' ? (
                    <PrimaryButton
                        size="small"
                        disabled={markingComplete}
                        onClick={() =>
                            markComplete({
                                variables: {
                                    override_id: integration.override_id,
                                    update: { status: 'COMPLETE' },
                                },
                            })
                        }
                    >
                        Mark Complete
                    </PrimaryButton>
                ) : null}
                <DeleteIntegrationButton
                    integration={integration}
                    orgShipperIds={orgShipperIds}
                    onDeleted={() => setNotification?.({ severity: 'success', message: 'Integration deleted' })}
                    onError={() => setNotification?.({ severity: 'error', message: 'Failed to delete integration' })}
                />
            </FieldRow>

            {!isEdi && <SpacerV1 />}

            {!isEdi && (
                <>
                    {configFields ? (
                        <div>
                            {configFields.map((field) => {
                                const { key, label, secret, type, tooltip } = field;
                                const tooltipAdornment = tooltip ? (
                                    <Tooltip title={tooltip} placement="top">
                                        <InfoOutlined
                                            css={css`
                                                font-size: 14px;
                                                color: #8d8d8d;
                                                cursor: help;
                                            `}
                                        />
                                    </Tooltip>
                                ) : null;

                                if (type === 'integer') {
                                    const currentVal = getFieldValue(field);
                                    const displayVal =
                                        currentVal !== undefined && currentVal !== null ? String(currentVal) : '';
                                    return (
                                        <DebouncedTextField
                                            key={key}
                                            label={label}
                                            size="small"
                                            variant="outlined"
                                            value={displayVal}
                                            onChange={(e) => handleIntegerChange(field, e.target.value)}
                                            inputProps={{ type: 'number', step: 1 }}
                                            css={css`
                                                margin-bottom: 0.5rem;
                                            `}
                                        />
                                    );
                                }

                                if (type === 'jsonb') {
                                    const text = jsonbText[key] ?? '';
                                    const valid = isJsonbValid(key);
                                    return (
                                        <DebouncedTextField
                                            key={key}
                                            label={label}
                                            size="small"
                                            variant="outlined"
                                            multiline
                                            minRows={3}
                                            maxRows={12}
                                            value={text}
                                            onChange={(e) => handleJsonbChange(field, e.target.value)}
                                            error={!valid}
                                            helperText={!valid ? 'Invalid JSON' : undefined}
                                            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                                            css={css`
                                                margin-bottom: 0.5rem;
                                                width: 100%;
                                            `}
                                        />
                                    );
                                }

                                if (type === 'boolean') {
                                    return (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={!!getFieldValue(field)}
                                                    onChange={(e) => setFieldValue(field, e.target.checked)}
                                                />
                                            }
                                            label={label}
                                            css={css`
                                                display: flex;
                                                margin-bottom: 0.5rem;
                                            `}
                                        />
                                    );
                                }

                                if (secret) {
                                    return (
                                        <DebouncedTextField
                                            key={key}
                                            label={label}
                                            size="small"
                                            variant="outlined"
                                            type="password"
                                            autoComplete="new-password"
                                            placeholder="Leave blank to keep existing"
                                            value={secrets[key] ?? ''}
                                            onChange={(e) => setSecrets((prev) => ({ ...prev, [key]: e.target.value }))}
                                            css={css`
                                                margin-bottom: 0.5rem;
                                            `}
                                        />
                                    );
                                }

                                if (field.prefix) {
                                    return (
                                        <DebouncedTextField
                                            key={key}
                                            label={label}
                                            size="small"
                                            variant="outlined"
                                            value={stripPrefix(field.prefix, getFieldValue(field))}
                                            onChange={(e) => {
                                                const rest = stripPrefix(field.prefix, e.target.value);
                                                setFieldValue(field, rest ? `${field.prefix}${rest}` : '');
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">{field.prefix}</InputAdornment>
                                                ),
                                                ...(tooltipAdornment ? { endAdornment: tooltipAdornment } : {}),
                                            }}
                                            css={css`
                                                margin-bottom: 0.5rem;
                                            `}
                                        />
                                    );
                                }

                                return (
                                    <DebouncedTextField
                                        key={key}
                                        label={label}
                                        size="small"
                                        variant="outlined"
                                        value={getFieldValue(field) ?? ''}
                                        onChange={(e) => setFieldValue(field, e.target.value)}
                                        InputProps={tooltipAdornment ? { endAdornment: tooltipAdornment } : undefined}
                                        css={css`
                                            margin-bottom: 0.5rem;
                                        `}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <pre
                            css={css`
                                font-size: 11px;
                                background: #f5f5f5;
                                padding: 0.5rem;
                                border-radius: 4px;
                                overflow-x: auto;
                                margin: 0;
                            `}
                        >
                            {JSON.stringify(integration.config, null, 2)}
                        </pre>
                    )}
                </>
            )}

            {!isEdi && configFields && (
                <EditActions>
                    <PrimaryButton
                        loading={saving}
                        disabled={
                            saving || (configFields || []).some((f) => f.type === 'jsonb' && !isJsonbValid(f.key))
                        }
                        onClick={() => callSaveFunction()}
                    >
                        Save
                    </PrimaryButton>
                </EditActions>
            )}
        </IntegrationBlock>
    );
};

const IntegrationRow = ({ integration }) =>
    integration.type === 'MANUAL' ? (
        <ManualSetupRow integration={integration} />
    ) : (
        <ConfiguredIntegrationRow integration={integration} />
    );

export default IntegrationRow;
